import { ActionIcon, Badge, Box, Group, Portal, Stack, Text, Title } from '@mantine/core';
import { IconArrowBackUp, IconMapPin, IconMessageCircle, IconX } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { FOUNDING_CITY_LOCTICIANS } from '../../workspace/services/hubsMapPrototype';
import { LocsMapCanvas } from '../../workspace/components/LocsMapCanvas';
import type { LocsMapMarker } from '../../workspace/types/mapProvider';

type AuthDiscoveryBackdropProps = {
  interactive: boolean;
};

export function AuthDiscoveryBackdrop({ interactive }: AuthDiscoveryBackdropProps) {
  const [selectedLocticianId, setSelectedLocticianId] = useState<string | null>(null);
  const selectedLoctician = useMemo(
    () => FOUNDING_CITY_LOCTICIANS.find((loctician) => loctician.id === selectedLocticianId) ?? null,
    [selectedLocticianId],
  );
  const markers = useMemo<LocsMapMarker[]>(() => FOUNDING_CITY_LOCTICIANS.map((loctician) => ({
    id: loctician.id,
    location: loctician.location,
    label: `${loctician.name}，${loctician.neighborhood}${loctician.decoratedMarker ? '，装饰标记' : ''}`,
    glyph: loctician.decoratedMarker ? '✦' : loctician.name.slice(0, 1).toUpperCase(),
    decorated: loctician.decoratedMarker,
    selected: loctician.id === selectedLocticianId,
  })), [selectedLocticianId]);

  return (
    <div
      className="auth-discovery-map"
      data-interactive={interactive ? 'true' : 'false'}
      aria-hidden={interactive ? undefined : true}
      inert={!interactive}
    >
      <LocsMapCanvas
        markers={markers}
        onSelectMarker={setSelectedLocticianId}
      />
      {interactive && selectedLoctician && (
        <Portal>
          <Box className="auth-map-loctician-card" role="dialog" aria-live="polite" aria-label={`${selectedLoctician.name}的发师预览`}>
            <ActionIcon
              className="auth-map-loctician-card__close"
              variant="subtle"
              radius="xl"
              aria-label="关闭发师预览"
              onClick={() => setSelectedLocticianId(null)}
            >
              <IconX size={17} />
            </ActionIcon>
            <Stack gap="md">
              <Group gap="sm" wrap="nowrap" align="flex-start">
                <Box className="auth-map-loctician-card__avatar" aria-hidden="true">
                  {selectedLoctician.name.slice(0, 1).toUpperCase()}
                </Box>
                <Box>
                  <Title order={2} className="auth-map-loctician-card__name">{selectedLoctician.name}</Title>
                  <Text size="xs" c="var(--locs-gold)">{selectedLoctician.studioLabel}</Text>
                </Box>
              </Group>
              <Group gap={6} className="auth-map-loctician-card__location" wrap="nowrap">
                <IconMapPin size={15} />
                <Text size="sm">{selectedLoctician.neighborhood} · {selectedLoctician.serviceArea}</Text>
              </Group>
              <Group gap={6}>
                {selectedLoctician.specialties.map((specialty) => (
                  <Badge key={specialty} variant="outline" color="terracotta" radius="xl">{specialty}</Badge>
                ))}
              </Group>
              <Text size="sm" c="var(--locs-text-muted)">{selectedLoctician.responseNote}</Text>
              <Group gap={8} className="auth-map-loctician-card__action" wrap="nowrap">
                <IconMessageCircle size={17} />
                <Text size="sm" fw={750}>登录后即可私信发师</Text>
              </Group>
              <Group gap={6} justify="center" className="auth-map-loctician-card__return">
                <IconArrowBackUp size={14} />
                <Text size="xs">点右上角“返回登录”继续联系</Text>
              </Group>
            </Stack>
          </Box>
        </Portal>
      )}
    </div>
  );
}
